/**
 * CountrySelectorPopup
 *
 * A modal shown on first visit that prompts the user to choose their
 * shipping country. Once dismissed or a country is selected, a flag in
 * localStorage prevents it from appearing again.
 *
 * Visibility logic:
 * - Show when: no `preferred_locale` cookie AND no `country_popup_seen` in localStorage
 * - Hide when: user selects a country OR clicks dismiss / backdrop
 */

import styled from '@emotion/styled';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useIntl, getCountryGroups, getLanguageLabel } from '@/lib/intl';
import { getCookie } from '@/lib/intl/IntlProvider';
import { countryCodeToFlagEmoji } from '@/components/CountrySelector/CountrySelector';
import type { CountryConfig } from '@template-project/storefront-shared';

const POPUP_SEEN_KEY = 'country_popup_seen';
const PREFERRED_LOCALE_COOKIE = 'preferred_locale';

function hasSeenPopup(): boolean {
  try {
    return localStorage.getItem(POPUP_SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function markPopupSeen(): void {
  try {
    localStorage.setItem(POPUP_SEEN_KEY, '1');
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
  }
}

interface AnimatedProps {
  $closing: boolean;
}

const Overlay = styled.div<AnimatedProps>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 24px;
  animation: ${({ $closing }) => ($closing ? 'cspFadeOut' : 'cspFadeIn')} 0.25s ease forwards;

  @keyframes cspFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes cspFadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Modal = styled.div<AnimatedProps>`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.radius.regular};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 420px;
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  padding: 32px 28px 28px;
  position: relative;
  animation: ${({ $closing }) => ($closing ? 'cspSlideDown' : 'cspSlideUp')} 0.25s ease forwards;

  @keyframes cspSlideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  @keyframes cspSlideDown {
    from { transform: translateY(0); opacity: 1; }
    to { transform: translateY(20px); opacity: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: 4px;
  line-height: 1;
  font-size: 20px;
  transition: color 0.15s;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 6px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 24px;
`;

const CountryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  min-height: 0;
`;

const CountryCard = styled.button<{ readonly active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  background: ${({ active, theme }) =>
    active ? theme.colors.background.secondary : theme.colors.background.primary};
  border: 2px solid
    ${({ active, theme }) => (active ? theme.colors.text.primary : theme.colors.border.color)};
  border-radius: ${({ theme }) => theme.radius.regular};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background-color 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.secondary};
    background: ${({ theme }) => theme.colors.background.secondary};
  }
`;

const FlagSpan = styled.span`
  font-size: 28px;
  line-height: 1;
`;

const CardDetails = styled.div`
  flex: 1;
`;

const CardCountryName = styled.span`
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CardCurrency = styled.span`
  display: block;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`;

const CardLanguages = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const CardLangButton = styled.button<{ readonly active: boolean }>`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 12px;
  font-weight: ${({ active }) => (active ? 600 : 400)};
  color: ${({ active, theme }) =>
    active ? theme.colors.text.primary : theme.colors.text.secondary};
  text-decoration: ${({ active }) => (active ? 'none' : 'underline')};
  transition: color 0.15s;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const CLOSE_ANIMATION_MS = 260;

export function CountrySelectorPopup() {
  const { locale, changeLocale } = useIntl();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const countryGroups = getCountryGroups();

  // Only show on the homepage — product/collection pages need unblocked interactions
  const isHomePage = location.pathname === '/' || /^\/[a-z]{2}-[A-Z]{2}\/?$/.test(location.pathname);

  useEffect(() => {
    if (!isHomePage) return;
    const hasCookie = Boolean(getCookie(PREFERRED_LOCALE_COOKIE));
    const hasSeen = hasSeenPopup();
    if (!hasCookie && !hasSeen) {
      setVisible(true);
    }
  }, [isHomePage]);

  const startClose = useCallback(() => {
    setIsClosing(true);
    closeTimerRef.current = setTimeout(() => {
      setVisible(false);
      setIsClosing(false);
    }, CLOSE_ANIMATION_MS);
  }, []);

  // Clean up any pending close timer on unmount.
  useEffect(() => {
    return () => clearTimeout(closeTimerRef.current);
  }, []);

  const handleSelect = useCallback(
    (config: CountryConfig) => {
      markPopupSeen();
      if (config.locale !== locale) {
        changeLocale(config.locale);
      }
      startClose();
    },
    [locale, changeLocale, startClose]
  );

  const handleDismiss = useCallback(() => {
    markPopupSeen();
    startClose();
  }, [startClose]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        handleDismiss();
      }
    },
    [handleDismiss]
  );

  if (!visible) {
    return null;
  }

  return (
    <Overlay onClick={handleOverlayClick} data-testid="country-popup-overlay" $closing={isClosing}>
      <Modal role="dialog" aria-labelledby="country-popup-title" aria-modal="true" $closing={isClosing}>
        <CloseButton onClick={handleDismiss} aria-label="Close" type="button">
          &times;
        </CloseButton>

        <Title id="country-popup-title">Choose your shipping country</Title>
        <Subtitle>Prices and delivery options vary by location.</Subtitle>

        <CountryList>
          {countryGroups.map((group) => {
            const isActiveCountry = group.locales.some((l) => l.locale === locale);
            const primaryLocale = group.locales[0];
            const hasMultipleLanguages = group.locales.length > 1;

            return (
              <CountryCard
                key={group.countryCode}
                active={isActiveCountry}
                onClick={hasMultipleLanguages ? undefined : () => handleSelect(primaryLocale)}
                type="button"
                style={hasMultipleLanguages ? { cursor: 'default' } : undefined}
              >
                <FlagSpan>{countryCodeToFlagEmoji(group.countryCode)}</FlagSpan>
                <CardDetails>
                  <CardCountryName>{group.displayName}</CardCountryName>
                  <CardCurrency>
                    {group.currency.format} {group.currency.code}
                  </CardCurrency>
                  {hasMultipleLanguages && (
                    <CardLanguages>
                      {group.locales.map((loc) => (
                        <CardLangButton
                          key={loc.locale}
                          active={loc.locale === locale}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(loc);
                          }}
                          type="button"
                        >
                          {getLanguageLabel(loc.language)}
                        </CardLangButton>
                      ))}
                    </CardLanguages>
                  )}
                </CardDetails>
              </CountryCard>
            );
          })}
        </CountryList>
      </Modal>
    </Overlay>
  );
}
